import {
  Tag, Users, MousePointerClick, Smartphone, MonitorPlay, MapPin, Sparkles, Store,
} from 'lucide-react';

/**
 * Google Ads campaign objectives, each mapped to the `advertisingChannelType`
 * the backend already accepts (google-ads-campaigns.service.ts:create).
 *
 * Unlike a YouTube-only picker, every objective here has a real branch: the
 * wizard reads `channelType` + `adKind` to decide which ad step to show.
 */
export type AdKind = 'search' | 'video' | 'display' | 'pmax' | 'shopping' | 'app' | 'none';

export interface GoogleObjective {
  key: string;
  title: string;
  desc: string;
  icon: typeof Tag;
  channelType: 'SEARCH' | 'DISPLAY' | 'VIDEO' | 'DEMAND_GEN' | 'PERFORMANCE_MAX' | 'SHOPPING' | 'MULTI_CHANNEL';
  adKind: AdKind;
  /**
   * The objective's workspace wizard — every objective has one now, under
   * src/pages/google-ads/<objective>/ (campaign, ad group and ad pages).
   */
  route: string;
  /** Workspace list page for this objective. */
  workspace: string;
  /** Bidding model — VIDEO campaigns bid per view; the rest per click. */
  bid: 'cpc' | 'cpv';
  /** Short list shown on the card so the operator knows what they'll build. */
  builds: string[];
  /** Set when the objective needs something we can't create from the CRM yet. */
  blocked?: string;
  /** Account-side prerequisite the wizard checks for and explains (not a lock). */
  requires?: string;
}

export const GOOGLE_OBJECTIVES: GoogleObjective[] = [
  {
    key: 'leads',
    title: 'Leads',
    desc: 'Get leads and other conversions by encouraging customers to take action',
    icon: Users,
    channelType: 'SEARCH',
    adKind: 'search',
    bid: 'cpc',
    route: '/google-ads/leads/create/campaign',
    workspace: '/google-ads/leads',
    builds: ['Search campaign', 'Ad groups + keywords', 'Responsive search ads', 'Attach a lead form'],
  },
  {
    key: 'traffic',
    title: 'Website traffic',
    desc: 'Get the right people to visit your website',
    icon: MousePointerClick,
    channelType: 'SEARCH',
    adKind: 'search',
    bid: 'cpc',
    route: '/google-ads/traffic/create/campaign',
    workspace: '/google-ads/traffic',
    builds: ['Search campaign', 'Ad groups + keywords', 'Responsive search ads'],
  },
  {
    key: 'youtube',
    title: 'YouTube video',
    desc: 'Drive visits and conversions on YouTube — in-stream, in-feed and Shorts',
    icon: MonitorPlay,
    channelType: 'DEMAND_GEN',
    adKind: 'video',
    bid: 'cpc',
    route: '/google-ads/youtube/create/campaign?objective=youtube',
    workspace: '/google-ads/youtube',
    builds: ['YouTube-only Demand Gen campaign', 'Location and language targeting', 'In-stream, in-feed and Shorts video ads'],
  },
  {
    key: 'awareness',
    title: 'Awareness & reach',
    desc: 'Show your brand to a broad audience across the Google Display Network',
    icon: Sparkles,
    channelType: 'DISPLAY',
    adKind: 'display',
    bid: 'cpc',
    route: '/google-ads/awareness/create/campaign',
    workspace: '/google-ads/awareness',
    builds: ['Display campaign', 'Ad groups with audience + content targeting', 'Responsive display ads (images + text)'],
  },
  {
    key: 'sales',
    title: 'Sales',
    desc: 'Drive sales online, in app, by phone, or in store',
    icon: Tag,
    channelType: 'PERFORMANCE_MAX',
    adKind: 'pmax',
    bid: 'cpc',
    route: '/google-ads/sales/create/campaign',
    workspace: '/google-ads/sales',
    builds: ['Performance Max campaign', 'Asset groups: headlines, descriptions, images, logos', 'Runs across Search, Display, YouTube, Gmail, Maps'],
  },
  {
    key: 'app',
    title: 'App promotion',
    desc: 'Get more installs, engagement and pre-registration for your app',
    icon: Smartphone,
    channelType: 'MULTI_CHANNEL',
    adKind: 'app',
    bid: 'cpc',
    route: '/google-ads/app-promotion/create',
    workspace: '/google-ads/app-promotion',
    builds: ['App campaign (Play or App Store)', 'Target-CPA install bidding', 'App ad with headlines, images and videos'],
  },
  {
    key: 'local',
    title: 'Local store visits',
    desc: 'Drive visits to local stores, including restaurants and dealerships',
    icon: MapPin,
    channelType: 'PERFORMANCE_MAX',
    adKind: 'pmax',
    bid: 'cpc',
    route: '/google-ads/local/create/campaign',
    workspace: '/google-ads/local',
    builds: ['Performance Max with store goals', 'Linked Business Profile locations', 'Asset groups with images + logos'],
    requires: 'A Google Business Profile linked to the Google Ads account (Tools → Linked accounts).',
  },
  {
    key: 'shopping',
    title: 'Product sales (Shopping)',
    desc: 'Promote your product feed across Google Shopping',
    icon: Store,
    channelType: 'SHOPPING',
    adKind: 'shopping',
    bid: 'cpc',
    route: '/google-ads/shopping/create/campaign',
    workspace: '/google-ads/shopping',
    builds: ['Standard Shopping campaign', 'Product groups (all products)', 'Product ads from your feed'],
    requires: 'A Merchant Center account with an approved feed, linked to the Google Ads account.',
  },
];

export const objectiveByKey = (k?: string | null) =>
  GOOGLE_OBJECTIVES.find((o) => o.key === k);

/** Where "Continue" on an objective card goes. */
export const objectiveRoute = (o: GoogleObjective) => o.route;

/** Which objective a Google campaign belongs to, from its channel type (SEARCH/PMAX share two objectives each). */
export const objectivesForChannel = (channelType?: string | null) =>
  GOOGLE_OBJECTIVES.filter((o) => o.channelType === channelType);
